import axios from 'axios';

// 修改 API_BASE_URL，使用Vite的环境变量
// 如果 VITE_API_URL 为空或未定义，使用空字符串（相对路径）
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// 创建一个axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30秒超时
  headers: {
    'Content-Type': 'application/json',
  },
});

export const AUTH_SESSION_EXPIRED_EVENT = 'alm:auth-session-expired';

let sessionExpiryDispatched = false;

export const resetSessionExpiryState = () => {
  sessionExpiryDispatched = false;
};

// API端点常量 - 确保所有路径都以 /api 开头
export const ENDPOINTS = {
  LOGIN: '/api/auth/login',
  VERIFY: '/api/auth/verify',
  CHANGE_PASSWORD: '/api/auth/change-password',
  GENERATE_LICENSE: '/api/licenses/generate',
  GET_LICENSES: (service) => `/api/licenses/${service}`,
  DELETE_LICENSE: (service, id) => `/api/licenses/${service}/${id}`,
  SERVER_COUNT: '/api/servers/count',
  SERVER_INFO: '/api/servers/info',
  DASHBOARD_STATS: '/api/dashboard/stats',
  RECENT_ACTIVITIES: '/api/activities/recent',
  SYSTEM_INFO: '/api/system/info',
  USER_PROFILE: '/api/user/profile',
  USER_SETTINGS: '/api/user/settings',
  SYSTEM_CONFIGURATION: '/api/admin/configuration',
  SYSTEM_CONFIGURATION_SECRET: '/api/admin/configuration/jwt-secret',
  SYSTEM_RESTART: '/api/admin/restart',
  USER_COUNT: '/api/users/count', // 添加用户数量端点
  
  // 现代化API端点
  SERVER_COUNT_MODERN: '/api/servers/count/modern',
  SERVER_INFO_MODERN: '/api/servers/info/modern',
  DASHBOARD_STATS_MODERN: '/api/dashboard/stats/modern',
  SYSTEM_INFO_MODERN: '/api/system/info/modern',
  SYSTEM_STATS_MODERN: '/api/system/stats/modern',
  
  // 添加之前定义的端点别名，以保持向后兼容
  generateLicense: '/api/licenses/generate',
  getLicenses: (service) => `/api/licenses/${service}`,
  deleteLicense: (service, id) => `/api/licenses/${service}/${id}`,
  getDashboardStats: '/api/dashboard/stats',
  getRecentActivities: '/api/activities/recent',
  getSystemInfo: '/api/system/info',
  getServerCount: '/api/servers/count',
  getServerInfo: '/api/servers/info',
  getUserCount: '/api/users/count',
};

// 创建兼容层，模拟原有的 fetchAPI 函数
export const fetchAPI = async (endpoint, options = {}) => {
  try {
    let response;
    const config = {
      headers: options.headers || {}
    };
    
    // 从 localStorage 获取 token 并添加到请求头
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // 根据请求方法调用不同的 axios 方法
    if (!options.method || options.method.toUpperCase() === 'GET') {
      response = await api.get(endpoint, config);
    } else if (options.method.toUpperCase() === 'POST') {
      // 检查是否有JSON字符串形式的body
      let data = options.body;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // console.error('Failed to parse body as JSON:', e);
        }
      }
      response = await api.post(endpoint, data, config);
    } else if (options.method.toUpperCase() === 'PUT') {
      response = await api.put(endpoint, options.body, config);
    } else if (options.method.toUpperCase() === 'DELETE') {
      response = await api.delete(endpoint, config);
    }
    
    // 返回响应数据 - 这会使旧代码继续工作
    return response.data;
  } catch (error) {
    // 保留会话过期标识，避免业务页面继续显示重复的请求失败提示
    if (error.isSessionExpired) {
      throw error;
    }

    // 如果是axios错误，则提取并抛出错误数据
    if (error.response && error.response.data) {
      throw error.response.data;
    }
    throw error;
  }
};

// 认证相关API
export const authAPI = {
  login: async (username, password) => {
    try {
      const response = await api.post(ENDPOINTS.LOGIN, { username, password });
      if (response.data && response.data.token) {
        resetSessionExpiryState();
        return response.data;
      } else {
        throw new Error('登录失败，未获取到有效Token');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('登录失败，服务器错误');
      }
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
  },

  verifyAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('未登录');
    }

    try {
      const response = await api.get(ENDPOINTS.VERIFY, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw error;
      }
      throw error;
    }
  },

  changePassword: async (username, currentPassword, newPassword) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('未登录');
    
    try {
      const response = await api.post(ENDPOINTS.CHANGE_PASSWORD, {
        username,
        currentPassword,
        newPassword
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.isSessionExpired) {
        throw error;
      }

      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('更改密码失败');
      }
    }
  },

  checkAuth: () => {
    return !!localStorage.getItem('token');
  }
};

// 添加请求拦截器自动添加token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器处理常见错误
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 统一处理401错误(未授权)
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isLoginRequest = requestUrl.includes(ENDPOINTS.LOGIN);

      // 登录接口返回401通常只是账号或密码错误，不应触发全局会话退出。
      if (!isLoginRequest) {
        error.isSessionExpired = true;

        if (!sessionExpiryDispatched && typeof window !== 'undefined') {
          sessionExpiryDispatched = true;
          window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
        }
      }
    }
    return Promise.reject(error);
  }
);

// 添加调试拦截器
api.interceptors.request.use(
  (config) => {
    // 生产环境中移除日志
    // // console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, config);
    return config;
  },
  (error) => {
    // // console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // 生产环境中移除日志
    // // console.log(`API Response: ${response.status} ${response.config.url}`, response.data);
    return response;
  },
  (error) => {
    // // console.error('API Response Error:', error);
    return Promise.reject(error);
  }
);

export default api;
